import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import classes from './products.module.scss';
import Button from '../../_common/Form/Buttons/Button';
import InputField from '../../_common/Form/TextFields/InputField';
import {
  getProductsRequest,
  productUploadXLSXFileRequest,
  productUploadZipDileRequest,
} from '../../../store/actions/products';
import ProductsTable from './ProductsTable/ProductsTable';
import { useQuery } from '../../../utils/hooks/useQuery';
import utils from '../../../utils/utils';
import Loader from '../../_common/Loader/Loader';
import { changeMenuPath } from '../../../store/actions/menu';
import Checkbox from '../../_common/Form/Checkbox/Checkbox';
import Modal from '../../_common/Modal/Modal';
import { ReactComponent as ClearSvg } from '../../../assets/icons/close.svg';

let timeout;

const Products = () => {
  const { pathname } = useLocation();
  const {
    query,
    setQuery,
    queryToStringify,
  } = useQuery();

  const dispatch = useDispatch();
  const {
    productList,
    productTotalPage,
  } = useSelector((store) => store.products);

  const [requestData, setRequestData] = useState({
    search: query.search || '',
    page: query.page || 1,
    limit: query.limit || 15,
    otc: query.otc || 0,
  });
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [sendFile, setSendFile] = useState({
    sheet: {},
    zip: {},
  });
  const [importProductLoading, setImportProductLoading] = useState(false);

  const {
    page,
    limit,
    search,
    otc,
  } = requestData;

  useEffect(() => {
    (async () => {
      const data = utils.deleteEmptyKeys({ ...requestData });

      await dispatch(getProductsRequest(data));

      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    dispatch(changeMenuPath('Product', `${pathname}${queryToStringify(query)}`));
  }, [query]);

  const onChangePageData = async (path, count) => {
    const data = utils.deleteEmptyKeys({
      ...requestData,
      [path]: count,
    });

    if (data.otc === undefined) {
      data.otc = 0;
    }

    if (path === 'limit') {
      data.page = 1;
    }

    await dispatch(getProductsRequest(data));

    setRequestData(data);

    setQuery(data);
  };

  const changeSearch = async (value) => {
    setLoading(true);
    setRequestData((prev) => ({
      ...prev,
      page: 1,
      search: value,
    }));

    if (value.trim()) {
      clearTimeout(timeout);

      timeout = setTimeout(async () => {
        await dispatch(getProductsRequest({
          ...requestData,
          page: 1,
          search: value,
        }));
      }, 400);
    } else {
      const data = utils.deleteEmptyKeys({
        ...requestData,
        page: 1,
        search: '',
      });

      if (data.otc === undefined) {
        data.otc = 0;
      }

      await dispatch(getProductsRequest(data));
    }

    setLoading(false);

    setQuery({
      ...requestData,
      page: 1,
      search: value,
    });
  };

  const clearSearch = async () => {
    const data = utils.deleteEmptyKeys({
      ...requestData,
      page: 1,
      search: '',
    });

    if (data.otc === undefined) {
      data.otc = 0;
    }

    setLoading(true);

    setRequestData((prev) => ({
      ...prev,
      page: 1,
      search: '',
    }));

    await dispatch(getProductsRequest(data));

    setLoading(false);

    setQuery({
      ...requestData,
      page: 1,
      search: '',
    });
  };

  const checkOTC = async (checked) => {
    setLoading(true);

    const data = utils.deleteEmptyKeys({
      ...requestData,
      page: 1,
      otc: +checked,
    });

    if (data.otc === undefined) {
      data.otc = 0;
    }

    setRequestData(data);

    setQuery({
      ...requestData,
      otc: +checked,
    });
    await dispatch(getProductsRequest(data));

    setLoading(false);
  };

  const closeModal = () => {
    setOpenModal(false);
    setSendFile({
      sheet: {},
      zip: {},
    });

    setImportProductLoading(false);
  };

  const clearFile = (e, path) => {
    e.preventDefault();
    if (!importProductLoading) {
      setSendFile((prev) => ({
        ...prev,
        [path]: {},
      }));
    }
  };

  const changeFileUpload = (file, path) => {
    setSendFile((prev) => ({
      ...prev,
      [path]: file,
    }));
  };

  const importFile = async () => {
    if (sendFile.sheet?.name || sendFile.zip?.name) {
      setImportProductLoading(true);

      const [xlsxRes, zipRes] = await Promise.all([
        sendFile.sheet?.name ? dispatch(productUploadXLSXFileRequest(sendFile)) : null,
        sendFile.zip?.name ? dispatch(productUploadZipDileRequest(sendFile)) : null,
      ]);

      if (zipRes?.status !== 'error' && zipRes) {
        toast.success(zipRes.payload.data.message);
      }
      if (xlsxRes?.status !== 'error' && xlsxRes) {
        toast.success(xlsxRes.payload.data.message);
      }

      closeModal();
    } else {
      toast.error('Please select file');
    }

    setImportProductLoading(false);
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <h2>Products</h2>

        <div className={classes.header_btn}>
          <Checkbox
            onChange={(checked) => checkOTC(checked)}
            className={classes.checkbox}
            checked={!!otc}
            label="OTC"
          />

          <Button
            onClick={() => setOpenModal(true)}
            title="Import product"
            type="button"
            className={classes.btn_import}
            downloadButton
            buttonColor="#5F6C8E"
          />

          <Button
            href="add"
            title="Add new product"
            type="button"
            className={classes.btn}
            addButton
            reverseColor
          />

          <Button
            href="order"
            title="Order"
            type="button"
            className={classes.btn}
          />

          <Button
            href="adjust"
            title="Adjust"
            type="button"
            className={classes.btn}
          />
        </div>
      </div>

      <div className={classes.search_block}>
        <div className={classes.search}>
          <InputField
            wrapperClassName={classes.search_field}
            label="Search"
            value={search}
            onChange={(value) => changeSearch(value)}
          />

          <Button
            onClick={clearSearch}
            title="Clear"
            type="button"
            clearButton
            className={classes.btn}
          />
        </div>

        {/* <Button */}
        {/*  onClick={() => {}} */}
        {/*  title="Assign to Me" */}
        {/*  type="button" */}
        {/*  reverseColor */}
        {/*  className={classes.btn} */}
        {/* /> */}
      </div>

      {loading
        ? <Loader className={classes.loader} />

        : (
          <ProductsTable
            data={productList}
            onChange={onChangePageData}
            totalPage={productTotalPage}
            limit={+limit}
            page={+page - 1}
            otc={otc}
          />
        )}

      <Modal
        open={openModal}
        onClose={closeModal}
        className={classes.import_modal}
      >
        <h1>Import Products</h1>

        <div className={classes.modal_div_wrapper}>
          <input
            type="file"
            id="addXLSX"
            accept=".xlsx"
            onChange={(e) => {
              changeFileUpload(e.target.files[0], 'sheet');

              e.target.value = '';
            }}
          />

          <label htmlFor="addXLSX" className={classes.modal_div}>
            {sendFile?.sheet?.name && (
            <ClearSvg
              className={classes.close}
              onClick={(e) => clearFile(e, 'sheet')}
            />
            )}

            <p>
              {sendFile?.sheet?.name || ' Add XLSX file'}
            </p>
          </label>

          <input
            type="file"
            id="addFile"
            accept=".zip"
            onChange={(e) => {
              changeFileUpload(e.target.files[0], 'zip');

              e.target.value = '';
            }}
          />

          <label htmlFor="addFile" className={classes.modal_div}>
            {sendFile?.zip?.name && (
            <ClearSvg
              className={classes.close}
              onClick={(e) => clearFile(e, 'zip')}
            />
            )}

            <p>
              {sendFile?.zip?.name || 'Add ZIP file'}
            </p>
          </label>
        </div>

        <Button
          onClick={importFile}
          title="Import products"
          type="button"
          reverseColor
          className={classes.modal_btn}
          loading={importProductLoading}
        />
      </Modal>
    </div>
  );
};
export default Products;
